<script setup>
// Overview (phase 4.5, D-P1): the kit's pieces for the current assistant — the period as an OptionSelect in the
// header, the notices as the kit's Alert, the summary as the kit's text block, six StatTiles (3×2, one column on a
// phone) with the change as a Badge, the coverage as the kit's Progress, and the metric detail in the kit's Sheet.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import PolarisAssistant from 'dashboard/api/polaris/assistant';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import RangeSelector from 'dashboard/components-next/polaris/pageComponents/overview/RangeSelector.vue';
import WelcomeCard from 'dashboard/components-next/polaris/pageComponents/overview/WelcomeCard.vue';
import MetricCard from 'dashboard/components-next/polaris/pageComponents/overview/MetricCard.vue';
import AssistantDrilldownDrawer from 'dashboard/components-next/polaris/pageComponents/overview/AssistantDrilldownDrawer.vue';
import KnowledgeCard from 'dashboard/components-next/polaris/pageComponents/overview/KnowledgeCard.vue';
import SalesPerformanceSection from 'dashboard/components-next/polaris/pageComponents/overview/SalesPerformanceSection.vue';
import InboxBanner from 'dashboard/components-next/polaris/pageComponents/overview/InboxBanner.vue';
import CoverageBanner from 'dashboard/components-next/polaris/pageComponents/overview/CoverageBanner.vue';

const { t } = useI18n();
const { formatFullNumber } = useNumberFormatter();
const route = useRoute();
// Drilldown is admin-only; the backend policy enforces the same restriction.
const { checkPermissions } = usePolicy();
const canDrilldown = computed(() => checkPermissions(['administrator']));

const selectedRange = ref('this_month');

const assistantId = computed(() => route.params.assistantId);
const stats = ref(null);
const isLoadingStats = ref(false);

const fetchStats = async () => {
  isLoadingStats.value = true;
  try {
    const { data } = await PolarisAssistant.getStats({
      assistantId: assistantId.value,
      range: selectedRange.value,
    });
    stats.value = data;
  } catch {
    stats.value = null;
  } finally {
    isLoadingStats.value = false;
  }
};

watch([selectedRange, assistantId], fetchStats, { immediate: true });

// `direction` says whether a rising trend is good ('up'), bad ('down'), or
// neutral, so we can colour the delta independently of its sign.
const resolveTrendGood = (trendValue, direction) => {
  if (direction === 'neutral' || trendValue === 0) return null;
  return direction === 'up' ? trendValue > 0 : trendValue < 0;
};

// Trend units mirror the backend pack mode: a relative percent change ('%') for
// :percent metrics, a percentage-point delta (' pts') for rate metrics packed as
// :point, and a plain number for :absolute counts like conversation depth.
const TREND_SUFFIX = { percent: '%', point: ' pts', absolute: '' };

// Hours-saved is reported in hours, but large values read better as days. Past
// 100h we switch the unit so the card stays legible.
const formatDuration = hours =>
  hours >= 100 ? `${Math.round(hours / 24)}d` : `${hours}h`;

const metricFor = (statKey, formatValue, direction, trendKind = 'percent') => {
  const data = stats.value?.[statKey];
  // "-" when not measured, never a zero that was not measured (the kit's StatTile rule, R2-7-10)
  if (!data) return { value: '-', trend: '', trendGood: null };

  const sign = data.trend > 0 ? '+' : '';
  return {
    value: formatValue(data.current),
    trend: `${sign}${data.trend}${TREND_SUFFIX[trendKind]}`,
    trendGood: resolveTrendGood(data.trend, direction),
  };
};

const metrics = computed(() => [
  {
    key: 'handled',
    metric: 'conversations_handled',
    label: t('POLARIS.OVERVIEW.METRICS.HANDLED.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.HANDLED.HINT'),
    ...metricFor('conversations_handled', v => formatFullNumber(v), 'up'),
  },
  {
    key: 'autoResolution',
    metric: 'auto_resolution_rate',
    label: t('POLARIS.OVERVIEW.METRICS.AUTO_RESOLUTION.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.AUTO_RESOLUTION.HINT'),
    ...metricFor('auto_resolution_rate', v => `${v}%`, 'up', 'point'),
  },
  {
    key: 'handoff',
    metric: 'handoff_rate',
    label: t('POLARIS.OVERVIEW.METRICS.HANDOFF.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.HANDOFF.HINT'),
    ...metricFor('handoff_rate', v => `${v}%`, 'down', 'point'),
  },
  {
    key: 'reopen',
    metric: 'reopen_rate',
    label: t('POLARIS.OVERVIEW.METRICS.REOPEN.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.REOPEN.HINT'),
    ...metricFor('reopen_rate', v => `${v}%`, 'down', 'point'),
  },
  {
    key: 'hoursSaved',
    label: t('POLARIS.OVERVIEW.METRICS.HOURS_SAVED.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.HOURS_SAVED.HINT'),
    ...metricFor('hours_saved', formatDuration, 'up'),
  },
  {
    key: 'depth',
    label: t('POLARIS.OVERVIEW.METRICS.DEPTH.LABEL'),
    hint: t('POLARIS.OVERVIEW.METRICS.DEPTH.HINT'),
    ...metricFor(
      'conversation_depth',
      v => v.toFixed(1),
      'neutral',
      'absolute'
    ),
  },
]);

const drilldown = ref({ metric: '', label: '', value: '' });
const isDrilldownOpen = ref(false);

const openDrilldown = metric => {
  drilldown.value = {
    metric: metric.metric,
    label: metric.label,
    value: metric.value,
  };
  isDrilldownOpen.value = true;
};

const closeDrilldown = () => {
  isDrilldownOpen.value = false;
};
</script>

<template>
  <PageLayout
    preview="folded"
    :header-title="$t('POLARIS.OVERVIEW.HEADER')"
    :is-empty="false"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.POLARIS"
  >
    <template #headerActions>
      <RangeSelector v-model="selectedRange" />
    </template>
    <template #paywall />
    <template #body>
      <div class="flex flex-col gap-6">
        <InboxBanner />

        <CoverageBanner :knowledge="stats?.knowledge" />

        <WelcomeCard :range="selectedRange" />

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            v-for="metric in metrics"
            :key="metric.key"
            :label="metric.label"
            :value="metric.value"
            :trend="metric.trend"
            :hint="metric.hint"
            :trend-good="metric.trendGood"
            :clickable="canDrilldown && Boolean(metric.metric)"
            :loading="isLoadingStats && !stats"
            @click="openDrilldown(metric)"
          />
        </div>

        <KnowledgeCard :knowledge="stats?.knowledge" />

        <SalesPerformanceSection :range="selectedRange" />
      </div>

      <AssistantDrilldownDrawer
        v-if="canDrilldown"
        :open="isDrilldownOpen"
        :assistant-id="assistantId"
        :metric="drilldown.metric"
        :metric-name="drilldown.label"
        :metric-value="drilldown.value"
        :range="selectedRange"
        @close="closeDrilldown"
      />
    </template>
  </PageLayout>
</template>
